/*
 * Copyright (C) Fraunhofer IESE 2021-2025 - Mher Ter-Tovmasyan
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { handleError } from "@slr/shared-library";
import {
  AcceptCreationRequest,
  CreateRequest,
  OrganizationDraftsApi,
  RejectCreationRequest,
  UpdateRequest,
} from "@slr/user-service-external-sdk";
import { useCallback, useMemo } from "react";
import { useGetUserServiceApiConfiguration } from "./user.service";

export const useOrganizationDraftApi = () => {
  const configuration = useGetUserServiceApiConfiguration();
  const apiInstance = useMemo(
    () => new OrganizationDraftsApi(configuration),
    [configuration]
  );

  const createOrganizationDraft = useCallback(
    (requestParameters: CreateRequest) =>
      apiInstance.create(requestParameters).catch(handleError),
    [apiInstance]
  );

  const getOrganizationDrafts = useCallback(
    () => apiInstance.getAll().catch(handleError),
    [apiInstance]
  );

  const acceptOrganizationDraft = useCallback(
    (requestParameters: AcceptCreationRequest) =>
      apiInstance.acceptCreation(requestParameters).catch(handleError),
    [apiInstance]
  );

  const rejectOrganizationDraft = useCallback(
    (requestParameters: RejectCreationRequest) =>
      apiInstance.rejectCreation(requestParameters).catch(handleError),
    [apiInstance]
  );

  const updateOrganizationDraft = useCallback(
    (requestParameters: UpdateRequest) =>
      apiInstance.update(requestParameters).catch(handleError),
    [apiInstance]
  );

  return {
    createOrganizationDraft,
    getOrganizationDrafts,
    acceptOrganizationDraft,
    rejectOrganizationDraft,
    updateOrganizationDraft,
  };
};
